import { useApolloClient } from "@apollo/client";
import { MenuItem, Text } from "@chakra-ui/react";
import React from "react";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useSubStatusTransition } from "src/hooks/useSubStatusTransition";
import { maybePluralize } from "src/services/format";
import * as GQL from "src/types/graphql";
import {
  FETCH_OFFER_WAITLIST_STATUS,
  GET_ASSIGNED_FORMS_BY_FORM_ID,
} from "../graphql/queries";
import { FormSchoolStatus } from "../types";
import { trackFormAction, trackFormActionError } from "./trackFormAction";

interface Props {
  selectedFormSchoolStatuses: FormSchoolStatus[];
  onRefetch?: () => void;
  isBulkAction?: boolean;
}

const confirmationBodyText = (count: number) =>
  maybePluralize(
    count,
    "Excluding this item from consideration will prevent it from receiving offers.",
    "Excluding these items from consideration will prevent them from receiving offers."
  );

export const ExcludeForm = ({
  selectedFormSchoolStatuses: selectedFormSchoolsStatuses,
  onRefetch,
  isBulkAction = false,
}: Props) => {
  const apolloClient = useApolloClient();
  const { mutateAsync: excludeForms } = FSQ.useExclude();
  const { isSubStatusTransitionAllowed } = useSubStatusTransition();

  const toast = useAvelaToast();

  const { confirm, confirmationDialog, setBody, setHeader } =
    useConfirmationDialog({
      header: null,
      body: null,
      cancelButton: {
        label: "Cancel",
      },
      confirmButton: {
        label: "Yes, exclude",
        colorScheme: "blue",
      },
      translate: true,
    });

  const handleOnClick = React.useCallback(
    async (evt: React.MouseEvent) => {
      const schoolForms = selectedFormSchoolsStatuses;
      const count = schoolForms.length;
      const trackArgs = {
        actionType: "EXCLUDE_FROM_OFFERS" as const,
        bulk: isBulkAction,
        count,
        async: false,
      };

      const formNoun = maybePluralize(count, "form");
      setHeader(
        <Text>{`Exclude ${count} ${maybePluralize(count, "item")}?`}</Text>
      );
      setBody(<Text>{confirmationBodyText(count)}</Text>);

      trackFormAction({ verb: "Clicked", ...trackArgs });

      if (await confirm()) {
        trackFormAction({ verb: "Confirmed", ...trackArgs });

        const loadingToastId = toast({
          title: `Working hard`,
          description: `Excluding ${count} ${formNoun}...`,
          status: "loading",
          duration: undefined,
        });

        try {
          await excludeForms({
            body: {
              data: {
                form_schools: schoolForms.map((schoolForm) => ({
                  form_school_rank_id: schoolForm.id,
                  form_id: schoolForm.form_id,
                  school_id: schoolForm.school_id,
                })),
              },
            },
          });
          await apolloClient.refetchQueries({
            include: [
              FETCH_OFFER_WAITLIST_STATUS,
              GET_ASSIGNED_FORMS_BY_FORM_ID,
            ],
          });

          if (onRefetch) onRefetch();

          toast.close(loadingToastId);
          toast({
            title: "Hooray",
            description: `${count} ${formNoun} excluded`,
            status: "success",
          });
          trackFormAction({ verb: "Completed", ...trackArgs });
        } catch (err: any) {
          trackFormActionError(err, trackArgs);
          toast.close(loadingToastId);
          toast.error({
            title: "Oops! Something went wrong!",
            description: "Check your network and try again.",
          });
          console.error(err);
        }
      } else {
        trackFormAction({ verb: "Canceled", ...trackArgs });
      }
    },
    [
      apolloClient,
      confirm,
      excludeForms,
      isBulkAction,
      onRefetch,
      selectedFormSchoolsStatuses,
      setBody,
      setHeader,
      toast,
    ]
  );
  return (
    <>
      <MenuItem
        textColor="gray.700"
        fontWeight="400"
        fontSize="sm"
        onClick={handleOnClick}
        isDisabled={
          !selectedFormSchoolsStatuses.every((result) =>
            isSubStatusTransitionAllowed(
              result.sub_status,
              GQL.form_school_rank_status_enum.NotConsidered
            )
          )
        }
      >
        Exclude
      </MenuItem>
      {confirmationDialog}
    </>
  );
};
